import { accessToken } from './auth.js';
/**
 * Control-plane client.
 *
 * Identity is a SERVER-SIGNED COOKIE, not a header this file can set. That is
 * the point: the app can ask to be called "Ada", but it cannot ask to *be* Ada.
 * Every request therefore sends credentials and nothing else identifying.
 */

export interface PublicRoom {
  id: string;
  title: string;
  description?: string;
  moderators: string[];
  speakers: string[];
  presenters: string[];
  profiles: Record<string, { name: string }>;
  stageOnly: boolean;
  videoEnabled: boolean;
  closed: boolean;
  raisedHands: string[];
  egresses: Array<{ egressId: string; orientation: string; destinationIds: string[] }>;
  destinations: Array<{ id: string; platform: string; label: string; urlPreview: string }>;
}

export type Capability =
  | 'room:create' | 'room:view' | 'room:join' | 'room:speak'
  | 'room:present' | 'room:moderate' | 'room:broadcast' | 'room:invite';

export interface Me {
  sub: string;
  name: string;
  role: 'moderator' | 'presenter' | 'speaker' | 'audience';
}

export interface Session {
  sub: string;
  name: string;
  operator: boolean;
}

export interface Presence {
  onStage: Array<{ sub: string; name: string }>;
  listening: number;
  total: number;
  raisedHands: Array<{ sub: string; name: string }>;
  live: boolean;
  recording: boolean;
  capabilities: Capability[];
}

export interface JoinGrant {
  token: string;
  url: string;
  room: PublicRoom;
  capabilities: Capability[];
  me: Me;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function call<T>(path: string, options: RequestInit = {}): Promise<T> {
  /*
   * A signed-in account travels as a bearer token; the anonymous session travels
   * as the cookie. Both are sent when both exist, and the server prefers the
   * account — so a host who signs in stops being a per-device stranger without
   * anything else in this file changing.
   */
  const token = await accessToken();
  const response = await fetch(path, {
    ...options,
    // The session cookie is httpOnly, so it travels only if we ask for it.
    credentials: 'same-origin',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      // Only when there is a body: Fastify rejects an empty JSON body, and a
      // body-less request framed as having one is refused by the proxy.
      ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    throw new ApiError(response.status, body.error || `Request failed (${response.status})`);
  }
  return body as T;
}

/** Establish (or read) the session. Safe to call on every load. */
export const getSession = (): Promise<Session> => call('/api/session');

/** Choose a display name. The subject is untouched — only the label changes. */
export const setName = (name: string): Promise<Session> =>
  call('/api/session', { method: 'POST', body: JSON.stringify({ name }) });

export const getRoom = (
  id: string,
): Promise<{
  room: PublicRoom;
  capabilities: Capability[];
  me: Me;
  /** Set when this is a daily show room from a previous day. */
  endedShow?: boolean;
  todayRoomId?: string;
}> => call(`/api/rooms/${encodeURIComponent(id)}`);

export const getPresence = (id: string): Promise<Presence> =>
  call(`/api/rooms/${encodeURIComponent(id)}/presence`);

export const join = (roomId: string): Promise<JoinGrant> =>
  call(`/api/rooms/${encodeURIComponent(roomId)}/token`, { method: 'POST', body: '{}' });

export const raiseHand = (roomId: string, raised: boolean): Promise<{ room: PublicRoom }> =>
  call(`/api/rooms/${encodeURIComponent(roomId)}/hand`, {
    method: 'POST',
    body: JSON.stringify({ raised }),
  });

/** Stage changes target a SUBJECT, never a display name. */
export const stageAction = (
  roomId: string,
  action: 'promote-speaker' | 'promote-presenter' | 'demote',
  sub: string,
): Promise<{ room: PublicRoom }> =>
  call(`/api/rooms/${encodeURIComponent(roomId)}/stage/${action}`, {
    method: 'POST',
    body: JSON.stringify({ sub }),
  });

/** Who the app thinks I am, and which day's show is current. */
export interface HostIdentity {
  host: boolean;
  hostId: string | null;
  name: string;
  sub: string;
  /**
   * True when this identity came from a signed-in ACCOUNT rather than the legacy
   * enrolled-device cookie. Both report host: true, so this is the only way to
   * notice that an account session has lapsed onto the old identity.
   */
  account: boolean;
  day: string;
  roomId: string;
}

export const hostMe = (): Promise<HostIdentity> => call('/api/host/me');

/**
 * The name the audience sees, stored against the ACCOUNT.
 *
 * Distinct from setName(), which names an anonymous session in its cookie. A
 * signed-in host has no such cookie in play, and without this their Apple ID
 * name would follow them into every room they join.
 */
export const setStageName = (name: string): Promise<{ name: string }> =>
  call('/api/host/name', { method: 'POST', body: JSON.stringify({ name }) });

/** Redeem an enrollment link. Once per phone; the session lasts a year. */
export const enrollHost = (token: string): Promise<{ host: string; name: string }> =>
  call('/api/host/enroll', { method: 'POST', body: JSON.stringify({ token }) });

/**
 * Go live: today's room, created if needed, joined either way.
 *
 * One call rather than create-then-join, because both hosts tap this within
 * seconds of each other and a client-side existence check would race.
 */
export const startTodaysShow = (name: string): Promise<JoinGrant & { day: string }> =>
  call('/api/host/show', { method: 'POST', body: JSON.stringify({ name }) });

/**
 * Start recording the show.
 *
 * Requires `room:broadcast` (moderators). Records to MP4 and writes the HLS
 * tier by default; destinations already registered on the room go live too.
 *
 * Note for callers: this fails with 412 if nobody is publishing yet. Egress
 * waits for a media start signal and gives up rather than writing an empty
 * file, so the UI must say "start after someone is on stage" rather than
 * treating it as a fault.
 */
export const startShow = (roomId: string): Promise<{ room: PublicRoom }> =>
  call(`/api/rooms/${encodeURIComponent(roomId)}/show/start`, {
    method: 'POST',
    // An explicit body: the route reads record/hls from it, and a body-less
    // POST framed as JSON is refused by the proxy.
    body: JSON.stringify({ record: true, hls: true }),
  });

/** Stop recording and end every egress on the room. */
export const stopShow = (roomId: string): Promise<{ room: PublicRoom }> =>
  call(`/api/rooms/${encodeURIComponent(roomId)}/show/stop`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

/**
 * Redeem a host/speaker invite.
 *
 * Binds the link's role to THIS session, after which the permission lives in
 * the room keyed by subject and the link stops mattering.
 */
export const redeemInvite = (
  token: string,
): Promise<{ room: PublicRoom; role: string; capabilities: Capability[] }> =>
  call('/api/invite/redeem', { method: 'POST', body: JSON.stringify({ token }) });

/**
 * A link that opens whichever show is running on the day it is used.
 *
 * Sent once and kept — unlike a dated invite, which points at one room id and is
 * dead by the next morning.
 */
export const createStandingInvite = (
  role: 'moderator' | 'presenter' | 'speaker',
): Promise<{ url: string; role: string; expiresInSeconds: number }> =>
  call('/api/invites/standing', { method: 'POST', body: JSON.stringify({ role }) });

/** Kills every standing link. Anyone already admitted keeps their role. */
export const revokeStandingInvites = (): Promise<{ revoked: boolean; epoch: number }> =>
  call('/api/invites/standing/revoke', { method: 'POST', body: JSON.stringify({}) });

export const createInvite = (
  roomId: string,
  role: 'moderator' | 'presenter' | 'speaker',
): Promise<{ url: string; role: string; expiresInSeconds: number }> =>
  call(`/api/rooms/${encodeURIComponent(roomId)}/invite`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });

/* ── recordings ──────────────────────────────────────────────────────────── */

export interface Take {
  key: string;
  /** Presigned, time-limited, plays directly from the media host. */
  url: string;
  downloadUrl: string;
  startedAt: string;
  sizeBytes: number;
}

export interface RecordingDay {
  day: string;
  roomId: string;
  takes: Take[];
}

/**
 * The archive. Host-only — an audience session gets a 401.
 *
 * The URLs inside are already signed and expire, so they are safe to hold in
 * memory but not to persist: a stale page would hand out dead links.
 */
export const listRecordings = (): Promise<{
  days: RecordingDay[];
  /** The hosts' messenger calls, recorded on the server. */
  calls: RecordingDay[];
  configured: boolean;
  linkTtlSeconds?: number;
}> => call('/api/recordings');
